/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { call, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Stream,
  DeviceProfile,
  OTTFileToDeviceProfiles,
  Recording,
  PlaybackLicenseData,
  RecordingDataAdapter,
  PlaybackLicenseResult,
  PlaybackContextApi,
  PlaybackAssetType,
  Ducks,
  Asset,
} from '@wit/vtv-x-foundation-lib';
import AuthNUtils from './authNUtils';
import AssetInfoUtils from '../../../../store/utils/assetInfoUtils';

function* getFileDeviceProfile(stream: Stream): SagaIterator<DeviceProfile | undefined> {
  const filesToDeviceProfiles: OTTFileToDeviceProfiles | undefined = yield select(
    Ducks.Dms.Selectors.getFilesToDeviceProfiles
  );

  if (filesToDeviceProfiles && Array.isArray(filesToDeviceProfiles[stream.fileType])) {
    const deviceProfiles = filesToDeviceProfiles[stream.fileType];
    return deviceProfiles[0]; // return the first or undefined
  }

  return undefined;
}

function* getPlaybackData(asset: Recording, stream: Stream): SagaIterator<PlaybackLicenseData> {
  // for recording we need to pass the dataAdater Object
  // the externalId property is mandatory
  let adapter: RecordingDataAdapter = {
    externalId: asset.recordingId.toString(),
  };

  const streamDeviceProfile: DeviceProfile | undefined = yield call(getFileDeviceProfile, stream);

  if (streamDeviceProfile) {
    // if there is a valid DeviceProfile for the stream fileType then add the costumerType property to the adapter
    adapter = { ...adapter, customerType: streamDeviceProfile };
  }

  const program: Asset | undefined = yield call(AssetInfoUtils.getProgramFromExternalId, asset.externalId);

  if (program?.id) {
    // if there is a valid program id for the recording then add it to the adapter
    adapter = { ...adapter, PROGRAM_ID: program.id.toString() };
  }

  const result: PlaybackLicenseResult = yield Ducks.Api.callApi(
    PlaybackContextApi.getPlaybackData,
    asset.recordingId,
    PlaybackAssetType.RECORDING,
    stream.id,
    adapter
  );

  if (!result.clearContent) {
    // update the token with the authN
    result.data.token = yield call(AuthNUtils.getAuthN);
  }

  return result.data;
}

function* getPlaybackLicenseLink(asset: Recording, stream: Stream): SagaIterator<PlaybackLicenseResult> {
  // for recording we need to pass the dataAdater Object
  // the externalId property is mandatory
  let adapter: RecordingDataAdapter = {
    externalId: asset.recordingId.toString(),
  };

  const streamDeviceProfile: DeviceProfile | undefined = yield call(getFileDeviceProfile, stream);

  if (streamDeviceProfile) {
    // if there is a valid DeviceProfile for the stream fileType then add the costumerType property to the adapter
    adapter = { ...adapter, customerType: streamDeviceProfile };
  }

  return yield Ducks.Api.callApi(
    PlaybackContextApi.getPlaybackUrl,
    asset.recordingId,
    PlaybackAssetType.RECORDING,
    stream.id,
    adapter
  );
}

export default {
  getPlaybackData,
  getPlaybackLicenseLink,
};
